/**
 * SPILLESTEDSREGISTERET (QA15-S E, B10, migrering 1260) — plattformens register over norske spillesteder
 * med saler, i ett eksemplar. Organisasjonene leser det gjennom portene; Control Room eier det.
 *
 * `county`/`municipality` kommer fra Kartverket. «Må ha» er `null` når registeret ikke vet, og ukjent
 * er aldri ja i et filter.
 */
import { z } from 'zod';

export const VENUE_DIRECTORY_KINDS = ['arena', 'concert_hall', 'club', 'culture_house'] as const;
export type VenueDirectoryKind = (typeof VENUE_DIRECTORY_KINDS)[number];

export const VENUE_DIRECTORY_STATUSES = ['verified', 'unverified'] as const;
export type VenueDirectoryStatus = (typeof VENUE_DIRECTORY_STATUSES)[number];

const url = z.url().max(1000);
const shortText = z.string().trim().max(200);
const count = z.number().int().min(0).max(1_000_000);

export const venueDirectoryImageSchema = z
  .object({
    id: z.uuid(),
    url: z.string(),
    depicts: z.enum(['room', 'building']),
    credit: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    roomId: z.uuid().nullable(),
  })
  .meta({ id: 'VenueDirectoryImage' });
export type VenueDirectoryImage = z.infer<typeof venueDirectoryImageSchema>;

export const venueDirectorySourceSchema = z.object({
  url: z.string().nullable(),
  fetchedAt: z.string().nullable(),
  kind: z.string().nullable(),
});
export type VenueDirectorySource = z.infer<typeof venueDirectorySourceSchema>;

/** Kontaktperson bare når kilden tydelig merker den. */
export const venueDirectoryContactSchema = z
  .object({
    name: z.string().nullable(),
    role: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    sourceUrl: z.string().nullable(),
  })
  .meta({ id: 'VenueDirectoryContact' });
export type VenueDirectoryContact = z.infer<typeof venueDirectoryContactSchema>;

/** Kortet i markedet for en sal i registeret (ikke i Tvitvi ennå). */
export const venueDirectoryCardSchema = z
  .object({
    directoryRoomId: z.uuid(),
    directoryId: z.uuid(),
    venueName: z.string(),
    roomName: z.string(),
    kind: z.enum(VENUE_DIRECTORY_KINDS),
    status: z.enum(VENUE_DIRECTORY_STATUSES),
    websiteUrl: z.string().nullable(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    municipality: z.string().nullable(),
    county: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    capacityStanding: z.number().int().nullable(),
    capacitySeated: z.number().int().nullable(),
    hasGrandPiano: z.boolean().nullable(),
    nightlinerParking: z.boolean().nullable(),
    wheelchairSpaces: z.number().int().nullable(),
    specUrl: z.string().nullable(),
    imageUrl: z.string().nullable(),
    imageCredit: z.string().nullable(),
    /** Organisasjonen som spør meldte inn stedet. */
    reportedByUs: z.boolean(),
  })
  .meta({ id: 'VenueDirectoryCard' });
export type VenueDirectoryCard = z.infer<typeof venueDirectoryCardSchema>;

export const venueDirectoryRoomSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  capacityStanding: z.number().int().nullable(),
  capacitySeated: z.number().int().nullable(),
  inTvitvi: z.boolean(),
});

/** Organisasjonens eget lag på stedet: det den skrev for seg selv. */
export const venueDirectoryOwnNotesSchema = z
  .object({
    contactName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .meta({ id: 'VenueDirectoryOwnNotes' });
export type VenueDirectoryOwnNotes = z.infer<typeof venueDirectoryOwnNotesSchema>;

/** «Om stedet» — registersalen med stedet, bildene og de andre salene. */
export const venueDirectoryRoomDetailSchema = venueDirectoryCardSchema
  .extend({
    roomNotes: z.string().nullable(),
    contact: venueDirectoryContactSchema.nullable(),
    sources: z.array(venueDirectorySourceSchema),
    images: z.array(venueDirectoryImageSchema),
    rooms: z.array(venueDirectoryRoomSummarySchema),
    /** Salen i Tvitvi registersalen nå er, når den som spør får se den. */
    linkedRoomId: z.uuid().nullable(),
    inTvitvi: z.boolean(),
    ownNotes: venueDirectoryOwnNotesSchema.nullable(),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .meta({ id: 'VenueDirectoryRoomDetail' });
export type VenueDirectoryRoomDetail = z.infer<typeof venueDirectoryRoomDetailSchema>;

/** Fylke med kommunene der det finnes saler, som på finn.no. */
export const venuePlaceSchema = z
  .object({
    county: z.string(),
    count: z.number().int().min(0),
    municipalities: z.array(z.object({ name: z.string(), count: z.number().int().min(0) })),
  })
  .meta({ id: 'VenuePlace' });
export type VenuePlace = z.infer<typeof venuePlaceSchema>;

/** «Meld inn et sted som mangler». Fylke og kommune slås opp i API-et. */
export const reportVenueRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    roomName: shortText.nullish(),
    kind: z.enum(VENUE_DIRECTORY_KINDS).optional(),
    address: shortText.nullish(),
    postalCode: z.string().trim().max(10).nullish(),
    city: z.string().trim().max(120).nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    capacityStanding: count.nullish(),
    capacitySeated: count.nullish(),
    websiteUrl: url.nullish(),
    notes: z.string().trim().max(4000).nullish(),
  })
  .meta({ id: 'ReportVenueRequest' });
export type ReportVenueRequest = z.infer<typeof reportVenueRequestSchema>;

export const reportVenueResponseSchema = z
  .object({
    directoryRoomId: z.uuid(),
    directoryId: z.uuid(),
    /** Stedet fantes alt som verifisert: ingenting nytt ble laget. */
    existed: z.boolean(),
  })
  .meta({ id: 'ReportVenueResponse' });
export type ReportVenueResponse = z.infer<typeof reportVenueResponseSchema>;

export const updateVenueDirectoryNotesRequestSchema = z
  .object({
    contactName: shortText.nullish(),
    email: z
      .email()
      .max(254)
      .nullish()
      .or(z.literal('').transform(() => null)),
    phone: z.string().trim().max(40).nullish(),
    notes: z.string().trim().max(4000).nullish(),
  })
  .meta({ id: 'UpdateVenueDirectoryNotesRequest' });
export type UpdateVenueDirectoryNotesRequest = z.infer<
  typeof updateVenueDirectoryNotesRequestSchema
>;

/* ------------------------------------------------------------------------- Control Room */

export const platformVenueListItemSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    kind: z.enum(VENUE_DIRECTORY_KINDS),
    status: z.enum(VENUE_DIRECTORY_STATUSES),
    city: z.string().nullable(),
    municipality: z.string().nullable(),
    county: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    roomCount: z.number().int(),
    imageCount: z.number().int(),
    openRequests: z.number().int(),
    reportCount: z.number().int(),
    linkedOrganization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    linkedRooms: z.number().int(),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .meta({ id: 'PlatformVenueListItem' });
export type PlatformVenueListItem = z.infer<typeof platformVenueListItemSchema>;

export const platformVenueListResponseSchema = z
  .object({
    items: z.array(platformVenueListItemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  })
  .meta({ id: 'PlatformVenueListResponse' });
export type PlatformVenueListResponse = z.infer<typeof platformVenueListResponseSchema>;

export const listPlatformVenuesQuerySchema = z
  .object({
    q: z.string().trim().max(120).optional(),
    status: z.enum(VENUE_DIRECTORY_STATUSES).optional(),
    county: z.string().trim().max(120).optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .meta({ id: 'ListPlatformVenuesQuery' });
export type ListPlatformVenuesQuery = z.infer<typeof listPlatformVenuesQuerySchema>;

export const platformVenueRoomSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  capacityStanding: z.number().int().nullable(),
  capacitySeated: z.number().int().nullable(),
  specUrl: z.string().nullable(),
  notes: z.string().nullable(),
  hasGrandPiano: z.boolean().nullable(),
  nightlinerParking: z.boolean().nullable(),
  wheelchairSpaces: z.number().int().nullable(),
  linkedRoomId: z.uuid().nullable(),
  linkedRoomName: z.string().nullable(),
  openRequests: z.number().int(),
});
export type PlatformVenueRoom = z.infer<typeof platformVenueRoomSchema>;

export const platformVenueDetailSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    /** 311.2: navnene til steder som er slått sammen inn i dette; søket finner dem. */
    aliases: z.array(z.string()).default([]),
    kind: z.enum(VENUE_DIRECTORY_KINDS),
    status: z.enum(VENUE_DIRECTORY_STATUSES),
    websiteUrl: z.string().nullable(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    municipality: z.string().nullable(),
    municipalityNumber: z.string().nullable(),
    county: z.string().nullable(),
    contact: venueDirectoryContactSchema.nullable(),
    sources: z.array(venueDirectorySourceSchema),
    notes: z.string().nullable(),
    linkedOrganization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    reportedBy: z.string().nullable(),
    verifiedAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    rooms: z.array(platformVenueRoomSchema),
    images: z.array(venueDirectoryImageSchema),
    reports: z.array(
      z.object({
        organizationId: z.uuid(),
        organizationName: z.string(),
        reportedAt: z.iso.datetime({ offset: true }),
      }),
    ),
  })
  .meta({ id: 'PlatformVenueDetail' });
export type PlatformVenueDetail = z.infer<typeof platformVenueDetailSchema>;

const nullableNumber = z.number().nullish();

export const savePlatformVenueRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    kind: z.enum(VENUE_DIRECTORY_KINDS).optional(),
    status: z.enum(VENUE_DIRECTORY_STATUSES).optional(),
    websiteUrl: url.nullish(),
    address: shortText.nullish(),
    postalCode: z.string().trim().max(10).nullish(),
    city: z.string().trim().max(120).nullish(),
    latitude: nullableNumber,
    longitude: nullableNumber,
    municipality: z.string().trim().max(120).nullish(),
    county: z.string().trim().max(120).nullish(),
    contact: venueDirectoryContactSchema.partial().nullish(),
    notes: z.string().trim().max(4000).nullish(),
  })
  .meta({ id: 'SavePlatformVenueRequest' });
export type SavePlatformVenueRequest = z.infer<typeof savePlatformVenueRequestSchema>;

export const savePlatformVenueRoomRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    capacityStanding: count.nullish(),
    capacitySeated: count.nullish(),
    specUrl: url.nullish(),
    notes: z.string().trim().max(4000).nullish(),
    hasGrandPiano: z.boolean().nullish(),
    nightlinerParking: z.boolean().nullish(),
    wheelchairSpaces: count.nullish(),
  })
  .meta({ id: 'SavePlatformVenueRoomRequest' });
export type SavePlatformVenueRoomRequest = z.infer<typeof savePlatformVenueRoomRequestSchema>;

export const setPlatformVenueImagesRequestSchema = z
  .object({
    images: z
      .array(
        z.object({
          url,
          depicts: z.enum(['room', 'building']),
          credit: shortText.nullish(),
          sourceUrl: url.nullish(),
          roomId: z.uuid().nullish(),
        }),
      )
      .max(40),
  })
  .meta({ id: 'SetPlatformVenueImagesRequest' });
export type SetPlatformVenueImagesRequest = z.infer<typeof setPlatformVenueImagesRequestSchema>;

export const mergePlatformVenueRequestSchema = z
  .object({ mergeId: z.uuid() })
  .meta({ id: 'MergePlatformVenueRequest' });
export type MergePlatformVenueRequest = z.infer<typeof mergePlatformVenueRequestSchema>;

export const linkPlatformVenueRequestSchema = z
  .object({ organizationId: z.uuid().nullable() })
  .meta({ id: 'LinkPlatformVenueRequest' });
export type LinkPlatformVenueRequest = z.infer<typeof linkPlatformVenueRequestSchema>;

/** Registersalen → en sal hos organisasjonen (`venueRoomId`), eller lag den (`createInVenueId`), eller løs (`null`). */
export const linkPlatformVenueRoomRequestSchema = z
  .object({
    venueRoomId: z.uuid().nullable().optional(),
    createInVenueId: z.uuid().optional(),
  })
  .refine((body) => (body.venueRoomId !== undefined) !== (body.createInVenueId !== undefined), {
    message: 'Exactly one of venueRoomId and createInVenueId.',
  })
  .meta({ id: 'LinkPlatformVenueRoomRequest' });
export type LinkPlatformVenueRoomRequest = z.infer<typeof linkPlatformVenueRoomRequestSchema>;

export const linkPlatformVenueRoomResponseSchema = z
  .object({ venueRoomId: z.uuid().nullable(), delivered: z.number().int().min(0) })
  .meta({ id: 'LinkPlatformVenueRoomResponse' });
export type LinkPlatformVenueRoomResponse = z.infer<typeof linkPlatformVenueRoomResponseSchema>;

export const platformOrganizationVenuesSchema = z
  .object({
    venues: z.array(
      z.object({
        id: z.uuid(),
        name: z.string(),
        rooms: z.array(z.object({ id: z.uuid(), name: z.string(), taken: z.boolean() })),
      }),
    ),
  })
  .meta({ id: 'PlatformOrganizationVenues' });
export type PlatformOrganizationVenues = z.infer<typeof platformOrganizationVenuesSchema>;

export const platformVenueRequestSchema = z.object({
  id: z.uuid(),
  organization: z.object({ id: z.uuid(), name: z.string() }),
  directoryId: z.uuid(),
  directoryRoomId: z.uuid(),
  venueName: z.string(),
  roomName: z.string(),
  requestedOn: z.iso.date(),
  status: z.string(),
  artistName: z.string().nullable(),
  message: z.string().nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  /** Registersalen er koblet: «Lever nå» sender forespørselen til huset. */
  deliverable: z.boolean(),
  linkedRoomName: z.string().nullable(),
});
export type PlatformVenueRequest = z.infer<typeof platformVenueRequestSchema>;

export const platformVenueRequestListSchema = z
  .object({ items: z.array(platformVenueRequestSchema) })
  .meta({ id: 'PlatformVenueRequestList' });
export type PlatformVenueRequestList = z.infer<typeof platformVenueRequestListSchema>;

export const platformVenueImportResultSchema = z
  .object({
    created: z.number().int(),
    updated: z.number().int(),
    rooms: z.number().int(),
    images: z.number().int(),
    skipped: z.number().int(),
  })
  .meta({ id: 'PlatformVenueImportResult' });
export type PlatformVenueImportResult = z.infer<typeof platformVenueImportResultSchema>;

/** Filen fra researchagentene, slik den lastes opp (`docs/qa/qa15s/E.md`). Løst validert: SQL-en sjekker resten. */
export const platformVenueImportRequestSchema = z
  .object({
    venues: z
      .array(z.object({ name: z.string().min(1).max(200) }).catchall(z.unknown()))
      .min(1)
      .max(2000),
  })
  .meta({ id: 'PlatformVenueImportRequest' });
export type PlatformVenueImportRequest = z.infer<typeof platformVenueImportRequestSchema>;

export const platformVenueSummarySchema = z
  .object({
    venues: z.number().int(),
    rooms: z.number().int(),
    unverified: z.number().int(),
    openRequests: z.number().int(),
    linked: z.number().int(),
    withoutPlace: z.number().int(),
  })
  .meta({ id: 'PlatformVenueSummary' });
export type PlatformVenueSummary = z.infer<typeof platformVenueSummarySchema>;
